import { useEffect, useState, useRef } from 'react';

export default function HeroSection() {
  const [active, setActive] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [textWidth, setTextWidth] = useState<number | null>(null);
  const heroRef = useRef<HTMLElement>(null);
  const textRowRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const triggered = useRef(false);

  useEffect(() => {
    const fire = () => {
      if (triggered.current) return;
      triggered.current = true;
      // 2重RAFで確実に初期スタイルをブラウザに描画させてからアニメーション開始
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setActive(true);
          setLoaded(true);
        });
      });
    };

    // splashFinishedイベントを待つ
    window.addEventListener('splashFinished', fire);

    // スプラッシュがない場合（直接アクセス等）のフォールバック：4秒後に強制発火
    const fallback = setTimeout(fire, 4000);

    return () => {
      window.removeEventListener('splashFinished', fire);
      clearTimeout(fallback);
    };
  }, []);

  // テキスト行の実際の幅を測定
  useEffect(() => {
    const measure = () => {
      if (gridRef.current) {
        setTextWidth(gridRef.current.getBoundingClientRect().width);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active]);

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMousePos({
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    };
    window.addEventListener('mousemove', handleMouse, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  return (
    <section ref={heroRef} className="texture-hero relative min-h-screen flex items-center overflow-hidden bg-warm-white">

      {/* ── 意味・言語をテーマにした装飾 ── */}

      {/* 大きな同心円群（右上）— 言語が広がる波紋 */}
      {[
        { lg: 560, sm: 200, colorA: 'rgba(212,122,46,0.16)', colorB: 'rgba(201,168,76,0.10)' },
        { lg: 740, sm: 280, colorA: 'rgba(201,168,76,0.10)', colorB: 'rgba(212,122,46,0.07)' },
        { lg: 920, sm: 360, colorA: 'rgba(212,122,46,0.07)', colorB: 'rgba(74,158,142,0.05)' },
        { lg: 1100, sm: 440, colorA: 'rgba(74,158,142,0.05)', colorB: 'rgba(212,122,46,0.04)' },
      ].map(({ lg: size, sm: smSize, colorA, colorB }, i) => (
        <div key={size} className="absolute pointer-events-none">
          {/* SP用 */}
          <div
            className="absolute rounded-full pointer-events-none block lg:hidden"
            style={{
              width: smSize, height: smSize,
              right: -smSize * 0.38, top: -smSize * 0.38,
              border: `1px solid ${i % 2 === 0 ? colorA : colorB}`,
              opacity: loaded ? 1 : 0,
              transition: `opacity 1s ease ${0.4 + i * 0.15}s`,
            }}
          />
          {/* PC用（マウスパララックス） */}
          <div
            className="absolute rounded-full pointer-events-none hidden lg:block"
            style={{
              width: size, height: size,
              right: -size * 0.38, top: -size * 0.38,
              border: `1px solid ${i % 2 === 0 ? colorA : colorB}`,
              opacity: loaded ? 1 : 0,
              transform: `translate(${(mousePos.x - 0.5) * -(3 + i * 2)}px, ${(mousePos.y - 0.5) * -(3 + i * 2)}px)`,
              transition: `opacity 1s ease ${0.4 + i * 0.15}s, transform 1.2s cubic-bezier(0.22,1,0.36,1)`,
            }}
          />
        </div>
      ))}

      {/* 右側グラデーション */}
      <div
        className="absolute right-0 top-0 w-full lg:w-[42%] h-full pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, transparent 0%, rgba(212,122,46,0.03) 40%, rgba(201,168,76,0.05) 100%)',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 1.5s ease 0.3s',
        }}
      />

      {/* ── 「意味の断片」— 細かい文字・記号の散らばり（PC） ── */}
      {[
        { char: '意', x: '62%', y: '18%', size: 11, color: 'rgba(212,122,46,0.18)', delay: 1.0, mx: -18, my: -12 },
        { char: '味', x: '71%', y: '28%', size: 9, color: 'rgba(201,168,76,0.15)', delay: 1.1, mx: -22, my: -15 },
        { char: '語', x: '58%', y: '42%', size: 10, color: 'rgba(74,158,142,0.15)', delay: 1.2, mx: -14, my: -10 },
        { char: '翻', x: '78%', y: '52%', size: 8, color: 'rgba(212,122,46,0.12)', delay: 1.3, mx: -26, my: -18 },
        { char: '訳', x: '66%', y: '65%', size: 11, color: 'rgba(201,168,76,0.14)', delay: 1.0, mx: -20, my: -14 },
        { char: '循', x: '82%', y: '35%', size: 9, color: 'rgba(74,158,142,0.12)', delay: 1.4, mx: -16, my: -11 },
        { char: '環', x: '54%', y: '75%', size: 8, color: 'rgba(212,122,46,0.10)', delay: 1.1, mx: -12, my: -8 },
        { char: '構', x: '74%', y: '72%', size: 10, color: 'rgba(201,168,76,0.12)', delay: 1.2, mx: -24, my: -16 },
        { char: '造', x: '88%', y: '62%', size: 7, color: 'rgba(212,122,46,0.10)', delay: 1.5, mx: -30, my: -20 },
        { char: '価', x: '60%', y: '55%', size: 9, color: 'rgba(74,158,142,0.10)', delay: 1.3, mx: -18, my: -12 },
        { char: '値', x: '85%', y: '22%', size: 8, color: 'rgba(201,168,76,0.12)', delay: 1.0, mx: -20, my: -14 },
        { char: '市', x: '50%', y: '32%', size: 7, color: 'rgba(212,122,46,0.08)', delay: 1.6, mx: -10, my: -7 },
        { char: '場', x: '92%', y: '45%', size: 9, color: 'rgba(74,158,142,0.10)', delay: 1.1, mx: -28, my: -19 },
      ].map((item, i) => (
        <div
          key={i}
          className="absolute pointer-events-none hidden lg:block"
          style={{
            left: item.x, top: item.y,
            fontFamily: 'serif',
            fontSize: item.size,
            color: item.color,
            letterSpacing: '0.05em',
            opacity: loaded ? 1 : 0,
            transform: `translate(${(mousePos.x - 0.5) * item.mx}px, ${(mousePos.y - 0.5) * item.my}px)`,
            transition: `opacity 0.8s ease ${item.delay}s, transform 1s cubic-bezier(0.22,1,0.36,1)`,
          }}
        >
          {item.char}
        </div>
      ))}

      {/* ── 細かいドットグリッド（右上・右半分） ── */}
      <div className="absolute right-0 top-0 w-full lg:w-[48%] h-[50%] lg:h-full pointer-events-none select-none overflow-hidden">
        <div
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'radial-gradient(circle, #D47A2E 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            opacity: loaded ? 0.07 : 0,
            transition: 'opacity 1.4s ease 0.6s',
          }}
        />
      </div>

      {/* ── 細かいドットグリッド（左下・薄め） ── */}
      <div className="absolute left-0 bottom-0 w-[50%] lg:w-[30%] h-[30%] lg:h-[40%] pointer-events-none select-none overflow-hidden">
        <div
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'radial-gradient(circle, #C9A84C 1px, transparent 1px)',
            backgroundSize: '18px 18px',
            opacity: loaded ? 0.07 : 0,
            transition: 'opacity 1.4s ease 0.8s',
          }}
        />
      </div>

      {/* ── 大きな菱形（右中央）— 意味の核 ── */}
      {/* SP用 */}
      <div
        className="absolute pointer-events-none block lg:hidden"
        style={{
          width: 70, height: 70, right: '8%', top: '30%',
          border: '1.5px solid rgba(212,122,46,0.30)',
          transform: 'rotate(45deg)',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.9s ease 1s',
        }}
      />
      <div
        className="absolute pointer-events-none block lg:hidden"
        style={{
          width: 30, height: 30, right: 'calc(8% + 20px)', top: 'calc(30% + 20px)',
          border: '1px solid rgba(201,168,76,0.35)',
          transform: 'rotate(45deg)',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.9s ease 1.1s',
        }}
      />
      {/* PC用（マウスパララックス） */}
      <div
        className="absolute pointer-events-none hidden lg:block"
        style={{
          width: 160, height: 160, right: '18%', top: '28%',
          border: '1.5px solid rgba(212,122,46,0.32)',
          transform: `rotate(45deg) translate(${(mousePos.x - 0.5) * -22}px, ${(mousePos.y - 0.5) * -16}px)`,
          opacity: loaded ? 1 : 0,
          transition: `opacity 0.9s ease 1s, transform 1.1s cubic-bezier(0.22,1,0.36,1)`,
        }}
      />
      <div
        className="absolute pointer-events-none hidden lg:block"
        style={{
          width: 70, height: 70, right: 'calc(18% + 45px)', top: 'calc(28% + 45px)',
          border: '1px solid rgba(201,168,76,0.38)',
          transform: `rotate(45deg) translate(${(mousePos.x - 0.5) * -28}px, ${(mousePos.y - 0.5) * -20}px)`,
          opacity: loaded ? 1 : 0,
          transition: `opacity 0.9s ease 1.1s, transform 1.1s cubic-bezier(0.22,1,0.36,1)`,
        }}
      />
      {/* 菱形の中心点 */}
      <div
        className="absolute pointer-events-none hidden lg:block"
        style={{
          width: 6, height: 6,
          right: 'calc(18% + 77px)', top: 'calc(28% + 77px)',
          backgroundColor: 'rgba(212,122,46,0.45)',
          transform: `rotate(45deg) translate(${(mousePos.x - 0.5) * -32}px, ${(mousePos.y - 0.5) * -22}px)`,
          opacity: loaded ? 1 : 0,
          transition: `opacity 0.9s ease 1.2s, transform 1.1s cubic-bezier(0.22,1,0.36,1)`,
        }}
      />

      {/* ── 細い格子線（右エリア）— 構造・設計を示す ── */}
      {[
        { x1: '52%', y1: '10%', x2: '52%', y2: '90%', color: 'rgba(212,122,46,0.06)', delay: 0.8 },
        { x1: '65%', y1: '10%', x2: '65%', y2: '90%', color: 'rgba(201,168,76,0.05)', delay: 0.9 },
        { x1: '78%', y1: '10%', x2: '78%', y2: '90%', color: 'rgba(212,122,46,0.04)', delay: 1.0 },
        { x1: '91%', y1: '10%', x2: '91%', y2: '90%', color: 'rgba(74,158,142,0.04)', delay: 1.1 },
      ].map((line, i) => (
        <div
          key={i}
          className="absolute pointer-events-none hidden lg:block"
          style={{
            left: line.x1, top: line.y1,
            width: 1,
            height: '80%',
            background: `linear-gradient(to bottom, transparent, ${line.color}, transparent)`,
            opacity: loaded ? 1 : 0,
            transition: `opacity 1s ease ${line.delay}s`,
          }}
        />
      ))}
      {[
        { x: '52%', y: '25%', color: 'rgba(212,122,46,0.06)', delay: 0.9 },
        { x: '52%', y: '50%', color: 'rgba(201,168,76,0.05)', delay: 1.0 },
        { x: '52%', y: '75%', color: 'rgba(74,158,142,0.04)', delay: 1.1 },
      ].map((line, i) => (
        <div
          key={i}
          className="absolute pointer-events-none hidden lg:block"
          style={{
            left: line.x, top: line.y,
            width: '48%',
            height: 1,
            background: `linear-gradient(to right, transparent, ${line.color}, transparent)`,
            opacity: loaded ? 1 : 0,
            transition: `opacity 1s ease ${line.delay}s`,
          }}
        />
      ))}

      {/* ── 浮遊する小さなドット群 ── */}
      {[
        { size: 12, right: '32%', top: '18%', color: 'rgba(212,122,46,0.45)', delay: 1.0, mx: -28, my: -20, sp: true },
        { size: 8,  right: '22%', top: '62%', color: 'rgba(201,168,76,0.50)', delay: 1.1, mx: -22, my: -16, sp: true },
        { size: 16, right: '44%', top: '72%', color: 'rgba(74,158,142,0.35)', delay: 1.2, mx: -18, my: -14, sp: false },
        { size: 6,  right: '15%', top: '35%', color: 'rgba(212,122,46,0.40)', delay: 1.3, mx: -32, my: -24, sp: true },
        { size: 10, right: '38%', top: '45%', color: 'rgba(201,168,76,0.30)', delay: 1.0, mx: -20, my: -15, sp: false },
        { size: 5,  right: '26%', top: '82%', color: 'rgba(74,158,142,0.45)', delay: 1.4, mx: -12, my: -8, sp: false },
        { size: 18, right: '52%', top: '22%', color: 'rgba(212,122,46,0.18)', delay: 0.9, mx: -14, my: -10, sp: false },
        { size: 4,  right: '12%', top: '55%', color: 'rgba(201,168,76,0.55)', delay: 1.5, mx: -36, my: -26, sp: true },
        { size: 7,  right: '48%', top: '58%', color: 'rgba(212,122,46,0.25)', delay: 1.2, mx: -20, my: -14, sp: false },
        { size: 5,  right: '35%', top: '88%', color: 'rgba(74,158,142,0.35)', delay: 1.6, mx: -12, my: -8, sp: false },
      ].map((dot, i) => (
        <div
          key={i}
          className={`absolute rounded-full pointer-events-none ${dot.sp ? 'block' : 'hidden lg:block'}`}
          style={{
            width: dot.size, height: dot.size,
            right: dot.right, top: dot.top,
            backgroundColor: dot.color,
            opacity: loaded ? 1 : 0,
            transform: `translate(${(mousePos.x - 0.5) * dot.mx}px, ${(mousePos.y - 0.5) * dot.my}px)`,
            transition: `opacity 0.8s ease ${dot.delay}s, transform 1s cubic-bezier(0.22,1,0.36,1)`,
          }}
        />
      ))}

      {/* ── 斜めライン群（意味が伝わる方向性） ── */}
      {[
        { right: '20%', top: '12%', height: 100, rotate: 28, color: 'rgba(212,122,46,0.30)', delay: 1.0, mx: -12, my: -8, sp: true },
        { right: '14%', top: '42%', height: 70,  rotate: -18, color: 'rgba(74,158,142,0.25)', delay: 1.1, mx: -10, my: -7, sp: false },
        { right: '36%', top: '60%', height: 55,  rotate: 45,  color: 'rgba(201,168,76,0.30)', delay: 1.2, mx: -14, my: -10, sp: false },
        { right: '48%', top: '15%', height: 90,  rotate: -35, color: 'rgba(212,122,46,0.22)', delay: 1.3, mx: -8, my: -6, sp: true },
        { right: '56%', top: '68%', height: 60,  rotate: 20,  color: 'rgba(74,158,142,0.20)', delay: 1.4, mx: -10, my: -7, sp: false },
        { right: '28%', top: '38%', height: 45,  rotate: -55, color: 'rgba(201,168,76,0.22)', delay: 1.1, mx: -16, my: -11, sp: false },
      ].map((line, i) => (
        <div
          key={i}
          className={`absolute pointer-events-none ${line.sp ? 'block' : 'hidden lg:block'}`}
          style={{
            width: 1.5, height: line.height,
            right: line.right, top: line.top,
            background: `linear-gradient(to bottom, transparent, ${line.color}, transparent)`,
            opacity: loaded ? 1 : 0,
            transform: `rotate(${line.rotate}deg) translate(${(mousePos.x - 0.5) * line.mx}px, ${(mousePos.y - 0.5) * line.my}px)`,
            transition: `opacity 0.8s ease ${line.delay}s, transform 1s cubic-bezier(0.22,1,0.36,1)`,
          }}
        />
      ))}

      {/* ── 小さな正方形群 ── */}
      {[
        { size: 7, right: '28%', top: '55%', delay: 1.2, mx: -20, my: -15, sp: false },
        { size: 5, right: '42%', top: '35%', delay: 1.3, mx: -24, my: -18, sp: true },
        { size: 9, right: '16%', top: '70%', delay: 1.1, mx: -16, my: -12, sp: true },
        { size: 4, right: '60%', top: '48%', delay: 1.4, mx: -12, my: -8, sp: false },
        { size: 6, right: '72%', top: '30%', delay: 1.0, mx: -18, my: -13, sp: false },
      ].map((sq, i) => (
        <div
          key={i}
          className={`absolute pointer-events-none ${sq.sp ? 'block' : 'hidden lg:block'}`}
          style={{
            width: sq.size, height: sq.size,
            right: sq.right, top: sq.top,
            backgroundColor: 'rgba(212,122,46,0.35)',
            opacity: loaded ? 1 : 0,
            transform: `rotate(15deg) translate(${(mousePos.x - 0.5) * sq.mx}px, ${(mousePos.y - 0.5) * sq.my}px)`,
            transition: `opacity 0.8s ease ${sq.delay}s, transform 1s cubic-bezier(0.22,1,0.36,1)`,
          }}
        />
      ))}

      {/* ── メインコンテンツ ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-28 md:pt-36 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-8">

            {/* Tag line */}
            <div
              className="flex items-center gap-5 mb-14"
              style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s' }}
            >
              <span
                className="block h-px bg-accent-teal"
                style={{ width: loaded ? '40px' : '0px', transition: 'width 0.8s cubic-bezier(0.22,1,0.36,1) 0.4s' }}
              />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">
                Meaning Design / Communicate the Value / Cultivate the Market
              </span>
            </div>

            {/* Headline — 価値創造 ≒ 市場創造 */}
            <h1 className="mb-10">

              {/* ── 英語ルビ行 ＋ 日本語行: 同じgridテンプレートで列を揃える ── */}
              <div ref={gridRef} style={{ display: 'inline-grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>

                {/* 英語ルビ行 左 */}
                <span
                  className="font-garamond italic text-muted-brown/85 tracking-widest text-right whitespace-nowrap"
                  style={{
                    fontSize: 'clamp(0.78rem, 1.5vw, 1.1rem)',
                    opacity: active ? 1 : 0,
                    transform: active ? 'translateX(0px)' : 'translateX(-60px)',
                    transition: active
                      ? 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 1.3s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 1.3s'
                      : 'none',
                  }}
                >
                  Communicate the Value
                </span>

                {/* 英語ルビ行 中央≒ */}
                <span
                  className="text-accent-teal/50 tracking-tight text-center whitespace-nowrap px-2"
                  style={{
                    fontFamily: '"Helvetica Neue", Arial, sans-serif',
                    fontWeight: 100,
                    fontSize: 'clamp(0.78rem, 1.5vw, 1.1rem)',
                    opacity: active ? 1 : 0,
                    transform: active ? 'scale(1)' : 'scale(0)',
                    transition: active
                      ? 'opacity 0.4s cubic-bezier(0.34,1.56,0.64,1) 1.6s, transform 0.5s cubic-bezier(0.34,1.56,0.64,1) 1.6s'
                      : 'none',
                  }}
                >
                  ≒
                </span>

                {/* 英語ルビ行 右 */}
                <span
                  className="font-garamond italic text-muted-brown/85 tracking-widest text-left whitespace-nowrap"
                  style={{
                    fontSize: 'clamp(0.78rem, 1.5vw, 1.1rem)',
                    opacity: active ? 1 : 0,
                    transform: active ? 'translateX(0px)' : 'translateX(60px)',
                    transition: active
                      ? 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 1.3s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 1.3s'
                      : 'none',
                  }}
                >
                  Cultivate the Market
                </span>

                {/* 日本語 左：価値創造 */}
                <div style={{ overflow: 'hidden', display: 'flex', justifyContent: 'flex-end' }}>
                  <span
                    className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-none text-3xl sm:text-4xl md:text-5xl lg:text-[4.2rem] whitespace-nowrap"
                    style={{
                      opacity: active ? 1 : 0,
                      transform: active
                        ? 'translateX(0px) skewX(0deg)'
                        : 'translateX(-200px) skewX(-12deg)',
                      transition: active
                        ? 'opacity 1.0s cubic-bezier(0.16,1,0.3,1) 0.0s, transform 1.0s cubic-bezier(0.16,1,0.3,1) 0.0s'
                        : 'none',
                    }}
                  >
                    価値創造
                  </span>
                </div>

                {/* 日本語 中央：≒ */}
                <span
                  className="text-accent-teal leading-none text-3xl sm:text-4xl md:text-5xl lg:text-[4.2rem] whitespace-nowrap text-center px-[0.28em]"
                  style={{
                    fontFamily: '"Helvetica Neue", Arial, sans-serif',
                    fontWeight: 100,
                    letterSpacing: '-0.04em',
                    opacity: active ? 1 : 0,
                    transform: active
                      ? 'scale(1) rotate(0deg)'
                      : 'scale(3.2) rotate(-20deg)',
                    filter: active ? 'blur(0px)' : 'blur(6px)',
                    transition: active
                      ? [
                          'opacity 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.75s',
                          'transform 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.75s',
                          'filter 0.6s ease 0.75s',
                        ].join(', ')
                      : 'none',
                  }}
                >
                  ≒
                </span>

                {/* 日本語 右：市場創造 */}
                <div style={{ overflow: 'hidden', display: 'flex', justifyContent: 'flex-start' }}>
                  <span
                    className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-none text-3xl sm:text-4xl md:text-5xl lg:text-[4.2rem] whitespace-nowrap"
                    style={{
                      opacity: active ? 1 : 0,
                      transform: active
                        ? 'translateX(0px) skewX(0deg)'
                        : 'translateX(200px) skewX(12deg)',
                      transition: active
                        ? 'opacity 1.0s cubic-bezier(0.16,1,0.3,1) 0.0s, transform 1.0s cubic-bezier(0.16,1,0.3,1) 0.0s'
                        : 'none',
                    }}
                  >
                    市場創造
                  </span>
                </div>
              </div>

              {/* ≒を起点に左右に伸びる分割ライン — テキスト実幅に合わせる */}
              <div
                className="flex items-center mt-2 mb-4"
                style={{ width: textWidth ?? '100%', gap: 0 }}
              >
                {/* 左ライン */}
                <span
                  className="block h-px bg-charcoal/20 flex-1"
                  style={{
                    transformOrigin: 'right center',
                    opacity: active ? 1 : 0,
                    transform: active ? 'scaleX(1)' : 'scaleX(0)',
                    transition: active ? 'opacity 0.1s ease 1.1s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 1.1s' : 'none',
                  }}
                />
                {/* 中央ドット */}
                <span
                  className="block rounded-full bg-accent-teal flex-shrink-0"
                  style={{
                    width: 5, height: 5,
                    marginLeft: '0.5em',
                    marginRight: '0.5em',
                    opacity: active ? 1 : 0,
                    transform: active ? 'scale(1)' : 'scale(0)',
                    transition: active
                      ? 'opacity 0.4s ease 1.05s, transform 0.5s cubic-bezier(0.34,1.56,0.64,1) 1.05s'
                      : 'none',
                  }}
                />
                {/* 右ライン */}
                <span
                  className="block h-px bg-charcoal/20 flex-1"
                  style={{
                    transformOrigin: 'left center',
                    opacity: active ? 1 : 0,
                    transform: active ? 'scaleX(1)' : 'scaleX(0)',
                    transition: active ? 'opacity 0.1s ease 1.1s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 1.1s' : 'none',
                  }}
                />
              </div>
            </h1>

            {/* Body */}
            <div
              className="mb-14 max-w-md"
              style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease 0.65s, transform 0.7s ease 0.65s' }}
            >
              <p className="font-sans-jp font-light text-charcoal/80 leading-relaxed-jp text-sm md:text-base mb-4">
                良いものをつくっても、伝わらなければ価値にならない。<br />
                伝わったとき、はじめて市場が生まれる。
              </p>
              <p className="font-sans-jp font-light text-muted-brown leading-relaxed-jp text-xs md:text-sm">
                広告・グラフィック・Web・映像をつくる会社として、<br />
                医療・福祉・教育・地域・文化など<br />
                公益性の高い領域の「価値」を、社会に届けています。
              </p>
            </div>

            {/* Buttons */}
            <div
              className="flex flex-col sm:flex-row flex-wrap gap-3"
              style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 0.8s, transform 0.7s ease 0.8s' }}
            >
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-accent-teal transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                相談してみる
              </a>
              <a
                href="/kangaekata"
                className="inline-flex items-center gap-3 px-7 py-3.5 border border-charcoal text-charcoal text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                私たちの考え方
              </a>
              <a
                href="/cases"
                className="inline-flex items-center gap-3 px-7 py-3.5 text-sm font-sans-jp text-muted-brown tracking-wide-jp hover:text-accent-teal transition-colors duration-200 whitespace-nowrap group cursor-pointer"
              >
                事例を見る
                <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
              </a>
            </div>
          </div>
        </div>

        {/* Side decoration */}
        <div
          className="absolute right-10 bottom-24 hidden lg:flex flex-col items-center gap-3"
          style={{ opacity: loaded ? 1 : 0, transition: 'opacity 1s ease 1s' }}
        >
          <span className="block w-px h-12 bg-divider" style={{ height: loaded ? '48px' : '0px', transition: 'height 0.8s cubic-bezier(0.22,1,0.36,1) 1.1s' }} />
          <span className="text-[10px] font-garamond italic text-muted-light tracking-widest-jp" style={{ writingMode: 'vertical-rl' }}>
            Designing Meaning
          </span>
          <span className="block w-px h-12 bg-divider" style={{ height: loaded ? '48px' : '0px', transition: 'height 0.8s cubic-bezier(0.22,1,0.36,1) 1.3s' }} />
        </div>

        {/* ── スクロールインジケーター ── */}
        <div
          className="absolute bottom-20 left-1/2 -translate-x-1/2 flex-col items-center gap-2 cursor-pointer group hidden sm:flex"
          style={{
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.8s ease 2.0s',
          }}
          onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
        >
          {/* SCROLL テキスト */}
          <span
            className="text-[9px] font-garamond italic text-muted-light tracking-[0.22em] uppercase group-hover:text-accent-teal transition-colors duration-300"
          >
            Scroll
          </span>

          {/* 縦ライン＋流れ落ちるドット */}
          <div className="relative flex flex-col items-center" style={{ width: 1, height: 48 }}>
            {/* 背景の静的ライン */}
            <span className="absolute inset-0 bg-divider" />
            {/* 流れ落ちるアニメーションライン */}
            <span
              className="absolute left-0 w-px bg-accent-teal"
              style={{
                animation: 'scrollDrop 1.8s cubic-bezier(0.4,0,0.6,1) infinite',
              }}
            />
          </div>

          {/* 下向き山形 */}
          <svg
            width="10" height="6" viewBox="0 0 10 6" fill="none"
            className="text-muted-light group-hover:text-accent-teal transition-colors duration-300"
            style={{ animation: 'scrollChevron 1.8s ease-in-out infinite' }}
          >
            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      {/* Bottom ticker — スマホでは非表示 */}
      <div
        className="absolute bottom-0 left-0 right-0 border-t border-divider bg-warm-white/80 backdrop-blur-sm overflow-x-auto hidden sm:block"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(10px)', transition: 'opacity 0.7s ease 1s, transform 0.7s ease 1s' }}
      >
        <div className="flex items-center gap-0 px-6 md:px-10" style={{ minWidth: 'max-content' }}>
          {['医療', '福祉', '教育', '地域', '文化'].map((item, i) => (
            <div key={item} className="flex items-center flex-shrink-0">
              {i > 0 && <span className="block w-px h-4 bg-divider mx-5 md:mx-6" />}
              <span
                className="py-4 text-xs font-sans-jp text-muted-brown tracking-widest-jp whitespace-nowrap hover:text-accent-teal transition-colors duration-200 cursor-default"
                style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.5s ease ${1.1 + i * 0.08}s` }}
              >
                {item}
              </span>
            </div>
          ))}
          <span className="block w-px h-4 bg-divider mx-5 md:mx-6 flex-shrink-0" />
          <span className="py-4 text-[10px] font-garamond italic text-muted-light tracking-widest-jp whitespace-nowrap" style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.5s ease 1.5s' }}>
            Healthcare · Welfare · Education · Community · Culture
          </span>
        </div>
      </div>
    </section>
  );
}
